import { PRIORITY_STATUS_LABEL } from "@/components/priorities/priorityCardTokens";
import { PriorityStatus, TeamMemberUpdateStatus } from "@/types";
import { differenceInCalendarDays, format, isSameYear } from "date-fns";
import {
  AlertCircle,
  CheckCircle2,
  Hourglass,
  ListTodo,
  PauseCircle,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Priority-status chips: filter each member's cards.
 * Active chips are tinted rather than fully saturated — four solid chips turned the filter row into
 * the loudest thing on the page, and this matches the status badges on the cards themselves.
 */
export const statusOptions: {
  label: string;
  value: PriorityStatus;
  icon: LucideIcon;
  activeClass: string;
}[] = [
  { label: PRIORITY_STATUS_LABEL.focus,      value: "focus",   icon: Zap,          activeClass: "bg-primary/10 text-primary ring-1 ring-primary/25" },
  { label: PRIORITY_STATUS_LABEL.queue,      value: "queue",   icon: ListTodo,     activeClass: "bg-info/10 text-info ring-1 ring-info/25" },
  { label: PRIORITY_STATUS_LABEL.done,       value: "done",    icon: CheckCircle2, activeClass: "bg-success/10 text-success ring-1 ring-success/25" },
  // Block Int / Block Ext sit next to each other so the pair reads as one distinction.
  { label: PRIORITY_STATUS_LABEL.blocked,    value: "blocked", icon: AlertCircle,  activeClass: "bg-destructive/10 text-destructive ring-1 ring-destructive/25" },
  { label: PRIORITY_STATUS_LABEL.waiting,    value: "waiting", icon: Hourglass,    activeClass: "bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/25" },
  { label: PRIORITY_STATUS_LABEL["on-hold"], value: "on-hold", icon: PauseCircle,  activeClass: "bg-warning/10 text-warning ring-1 ring-warning/25" },
];

/**
 * Team-update filter: filters PEOPLE, not cards. The two non-"all" values are mutually exclusive —
 * only one can be active at a time, so selecting either always clears the other.
 *
 * `needs-update` means literally "has not posted an update this week" (`updatedThisWeek === false`)
 * — narrower than the BE's `needs_update` state, which also covers people who posted but have no
 * Focus task. That keeps the header pill count and the filtered rows exactly the same set.
 *
 * `missing-focus-queue` means the member fails the 1 Focus + 2 Queue board-completeness rule (see
 * `isMissingFocusQueue`) — independent of whether they posted an update this week.
 */
export type UpdateFilter = "all" | "needs-update" | "missing-focus-queue";

export function needsUpdate(status: TeamMemberUpdateStatus): boolean {
  return !status.updatedThisWeek;
}

/**
 * True when a member's board is short of the standing rule — 1 Focus + 2 Queue. Returns `false`
 * (not "missing") when either count is undefined, since the backend may not send them yet; showing
 * a flag built on absent data would be a false positive, not a hidden feature.
 *
 * Isolated in one helper so a rule change (e.g. 2 Queue → 3 Queue) is a one-line diff.
 */
export function isMissingFocusQueue(status: TeamMemberUpdateStatus): boolean {
  if (status.focusCount === undefined || status.queueCount === undefined) return false;
  return status.focusCount < 1 || status.queueCount < 2;
}

/**
 * Which half of the rule a member actually fails — a member holding a Focus item but only 1 Queue
 * item is "Missing Queue", not "Missing Focus". The badge must name the real gap: showing the
 * wrong half reads as a bug (a member with a Focus item still sees "Missing Focus"), which is
 * exactly what a fixed "Missing Focus" label did before this. Returns `undefined` when the member
 * passes, or when either count is absent.
 */
export function missingFocusQueueLabel(status: TeamMemberUpdateStatus): string | undefined {
  if (status.focusCount === undefined || status.queueCount === undefined) return undefined;
  const missingFocus = status.focusCount < 1;
  const missingQueue = status.queueCount < 2;
  if (missingFocus && missingQueue) return "Missing Focus & Queue";
  if (missingFocus) return "Missing Focus";
  if (missingQueue) return "Missing Queue";
  return undefined;
}

export type UpdateTone = "green" | "amber" | "red";

export const updateToneClass: Record<UpdateTone, string> = {
  green: "text-success",
  amber: "text-amber-600",
  red: "text-destructive",
};

/**
 * "Today" / "Yesterday" / "9 days ago" / "11 Jul" / "11 Jul 2025" / "Never" — one phrase, never
 * two. The year is dropped for the current year — repeating "2026" on every row of a page titled
 * "Week 33" is the kind of redundancy that pushes rows onto a second line for no new information.
 */
function lastUpdateText(lastUpdateAt?: string): string {
  if (!lastUpdateAt) return "Never";
  const date = new Date(lastUpdateAt);
  if (Number.isNaN(date.getTime())) return "Never";
  const days = differenceInCalendarDays(new Date(), date);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return format(date, isSameYear(date, new Date()) ? "d MMM" : "d MMM yyyy");
}

/**
 * The member's weekly state as ONE line: `Last report: 9 days ago`, coloured.
 *
 * The tone tracks the update itself — green is `updatedThisWeek`, the same field the summary counts,
 * so a green line can never appear inside the "needs update" filter. Missing-Focus is its own
 * "Missing Focus" badge (see `isMissingFocusQueue`), not repeated here as a second note.
 */
export function updateSignal(status: TeamMemberUpdateStatus): {
  tone: UpdateTone;
  label: string;
  /**
   * The "gone completely quiet" flag — set ONLY when there is NO report this week AND NO logged
   * activity this week either. A member with no report but SOME activity this week is quietly
   * working, which needs no extra flag; this line exists purely to separate that person from one
   * who has touched nothing at all, the harder signal a manager actually needs to catch.
   */
  activityText?: string;
} {
  const reportDate = status.lastUpdateAt ? new Date(status.lastUpdateAt) : null;
  const days = reportDate ? differenceInCalendarDays(new Date(), reportDate) : null;

  // Amber = behind, but only just. Red = a fortnight or more, or never.
  // Tone tracks the REPORT only — a member who edits priorities all week but never reports must
  // not turn green, or editing would substitute for the weekly report.
  const tone: UpdateTone = status.updatedThisWeek
    ? "green"
    : days != null && days <= 13
      ? "amber"
      : "red";

  // "This week" for activity mirrors the same 7-day window `updatedThisWeek` uses for reports —
  // there's no activity-specific week boundary from the backend, so this is the closest match.
  const activityDate = status.lastActivityAt ? new Date(status.lastActivityAt) : null;
  const activityDays = activityDate ? differenceInCalendarDays(new Date(), activityDate) : null;
  const hasActivityThisWeek = activityDays != null && activityDays <= 7;

  return {
    tone,
    label: `Last report: ${lastUpdateText(status.lastUpdateAt)}`,
    activityText:
      !status.updatedThisWeek && activityDate != null && !hasActivityThisWeek
        ? lastUpdateText(status.lastActivityAt)
        : undefined,
  };
}
